import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Cell,
    Column,
    ColumnResizer,
    ResizableTableContainer,
    Row,
    Table,
    TableBody,
    TableHeader,
    Text
} from 'react-aria-components';
import api from "../../utils/api.js";
import "./DatabasePreview.css"
import {LabeledSection} from "../../components/Fields/LabeledTextField.js";
import {LuEye} from "react-icons/lu";
import DefaultDropButton from "../../components/Buttons/DefaultDropButton/DefaultDropButton.js";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner.js";
import {decryptMessage, getPublicKey} from "../../utils/security.js";

export function useWebSocket<T = any>(
    url: string,
    onMessage: (data: T) => void,
    protocols?: string | string[]
) {
    const [isOpen, setIsOpen] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
    const onMessageRef = useRef(onMessage);

    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        const ws = new WebSocket(url, protocols);
        wsRef.current = ws;

        ws.onopen = () => {
            setIsOpen(true);
            console.log("✅ WebSocket connected:", url);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                onMessageRef.current(data);
            } catch (e) {
                console.warn("Invalid JSON from WebSocket:", event.data);
            }
        };

        ws.onclose = () => {
            setIsOpen(false);
            console.warn("⚠️ WebSocket closed. Reconnecting in 3s...");
            reconnectTimer.current = setTimeout(connect, 3000);
        };

        ws.onerror = (err) => {
            console.error("❌ WebSocket error:", err);
            ws.close(); // triggers onclose -> reconnect
        };
    }, [url, protocols]);

    useEffect(() => {
        connect();
        return () => {
            reconnectTimer.current && clearTimeout(reconnectTimer.current);
            wsRef.current?.close();
        };
    }, [connect]);

    const send = useCallback((data: any) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(data));
        } else {
            console.warn("⛔ Cannot send message — WebSocket not open.");
        }
    }, []);

    return { send, isOpen };
}

type Message = {
    message_type: string;
    payload: any;
};

type DataTableProps = {
    columns: string[];
    rows: Record<string, any>[];
    unique_table_id: string
};

function getStoredWidths(id: string, cols: string[]): number[] {
    const raw = localStorage.getItem(`table-widths-${id}`);
    try {
        const parsed: number[] = raw ? JSON.parse(raw) : [];
        if (parsed.length === cols.length) return parsed;
    } catch {/* fall through */}
    return Array(cols.length).fill(150);          // default width
}

function DataTable({ columns, rows, unique_table_id}: DataTableProps) {
    let [widths, setWidths] = useState<number[]>([]);

    useEffect(() => {
        const storedWidths = getStoredWidths(unique_table_id, columns);
        setWidths(storedWidths);
    }, [columns, unique_table_id]);

    function _sanitize(value: any): string {
        if (typeof value === 'object') {
            return JSON.stringify(value);
        } else if (typeof value === 'string') {
            return value;
        }
        return String(value);
    }

    function onResize(newWidths) {
        const newWidthsArray = Array.from(newWidths.values());
        if (newWidthsArray.length !== columns.length) {
            console.warn("Column widths do not match the number of columns.");
            return;
        }
        setWidths(newWidthsArray)
        console.log("Column widths updated:", newWidthsArray);
    }

    function onResizeEnd(newWidths) {
        const newWidthsArray = Array.from(newWidths.values());
        if (newWidthsArray.length !== columns.length) {
            console.warn("Column widths do not match the number of columns.");
            return;
        }
        setWidths(newWidthsArray)
        localStorage.setItem(
            `table-widths-${unique_table_id}`,
            JSON.stringify(widths)
        );
        console.log("Column widths saved to localStorage:", widths);
    }

    return <ResizableTableContainer
        onResize={onResize}
        onResizeEnd={onResizeEnd}
    >
        <Table
            className="striped-table"
            aria-label="Database Preview"
        >
        <TableHeader>
        {columns.map((column, index) => (<>
            <Column isRowHeader key={column} width={widths[index]}>
                <div className="flex-wrapper">
                    <span tabIndex={-1} className="column-name">
                        {column.replace(/_/g, ' ')}
                    </span>
                    <ColumnResizer />
                </div>
            </Column>
        </>))}
        </TableHeader>
        <TableBody>
            {rows?rows.map((row, rowIdx) => (
            <Row key={rowIdx}>
                {columns.map((col) => <Cell key={col}>
                    {_sanitize(row[col]) ?? ''}
                </Cell>)}
            </Row>
            )) : null}
        </TableBody>
    </Table>
    </ResizableTableContainer>
}

function setDefaultVal(dbId:string, key: string, value: string) {
    const storageKey = `database_preview_${dbId}_${key}`;
    localStorage.setItem(storageKey, value);
}

function getDefaultVal(dbId:string, key: string, defaultVal: string): string {
    const storageKey = `database_preview_${dbId}_${key}`;
    const storedValue = localStorage.getItem(storageKey);

    let val = defaultVal;
    if (storedValue !== null) {
        val = storedValue;
    }

    return val;
}

function DatabasePreview({server}: {server:{
    id:string,
    tables: {name: string, tableSchema: string}[],
    usernames: string[]
}}) {
    const [schemas, setSchemas] = React.useState([])
    const [selectedSchema, setSelectedSchema] = React.useState(getDefaultVal(server.id, "schema", null))
    const [tableNames, setTableNames] = React.useState([])
    const [selectedTableName, setSelectedTableName] = React.useState(getDefaultVal(server.id, "table", null))
    const [users, setUsers] = React.useState([])
    const [selectedUser, setSelectedUser] = React.useState(getDefaultVal(server.id, "user", null))
    const [previewData, setPreviewData] = React.useState(null)
    const [loadingPreviewData, setLoadingPreviewData] = React.useState(false)

    let wsUrl = `wss://api.veilstream.com/database/${server.id}/preview/ws/browser`
    if (window.location.hostname === "localhost") {
        wsUrl = `ws://localhost:2000/database/${server.id}/preview/ws/browser`
    }

    const { send, isOpen } = useWebSocket<Message>(wsUrl, onMsg,);

    // pull preview on load
    useEffect(() => {
        if(isOpen) {
            if (selectedSchema !== null && selectedTableName !== null && selectedUser !== null) {
                getConfigurationPreview()
            }
        }
    }, [isOpen]);

    function onMsg(data: Message) {
        if (data.message_type === "preview_response"){
            if (data.encrypted_results !== undefined && data.encrypted_results !== null) {
                const decryptedData = decryptMessage(data.encrypted_results)
                data = JSON.parse(decryptedData)
            }
            setPreviewData(data)
            setLoadingPreviewData(false)
        }
        console.log(data)
    }

    async function getDatabasePreviewConfiguration(
        databaseId: string,
        schema: string,
        table: string,
        user: string,
        encrypted: boolean = false,
    ) {
        const payload = {
            message_type: "preview_request",
            database_id: databaseId,
            schema_name: schema,
            table_name: table,
            user_name: user,
            count: "20",
            encrypted: false,
            public_key: null,
        }
        if (encrypted) {
            payload.encrypted = true
            payload.public_key = getPublicKey()
        }

        send(payload)
        setPreviewData(null)
        setLoadingPreviewData(true)
    }

    useEffect(() => {
        const schemas = []
        server.tables.forEach(table => {
            if (!schemas.includes(table.tableSchema)) {
                schemas.push(table.tableSchema)
            }
        })
        setSchemas(schemas)
        const newSelectedSchema = getDefaultVal(server.id, "schema", schemas[0] || null)

        const newTableNames = []
        server.tables.forEach(table => {
            if (table.tableSchema === newSelectedSchema) {
                newTableNames.push(table.name)
            }
        })
        setTableNames(newTableNames)


        const users = []
        server.usernames.forEach((username) => {
            users.push({
                name: username,
                id: username, // Assuming the username is the unique identifier
            });
        })
        setUsers(users)
    }, []);

    useEffect(() => {
        if(selectedSchema === null && schemas.length > 0) {
            setSelectedSchema(schemas[0] || null);
        }
    }, [schemas]);

    useEffect(() => {
        if(selectedTableName === null && schemas.length > 0) {
            updatedTablesList(schemas[0])
            if ( schemas[0].tables && schemas[0].tables.length > 0) {
                setSelectedTableName(schemas[0].tables[0].name);
            }
        }
    }, [schemas, server]);

    useEffect(() => {
        if(selectedUser === null && users.length > 0) {
            setSelectedUser(users[0].name || null);
        }
    }, [users]);

    useEffect(() => {
        getConfigurationPreview()
    }, [selectedSchema, selectedTableName, selectedUser]);

    function updatedTablesList(schemaName: string) {
        const tableNames = []
        server.tables.forEach(table => {
            if (table.tableSchema === schemaName) {
                tableNames.push(table.name)
            }
        })
        setTableNames(tableNames)
    }

    async function getConfigurationPreview() {
        if(selectedSchema === null) {
            return
        }
        if(selectedTableName === null) {
            return
        }
        if(selectedUser === null) {
            return
        }
        return getDatabasePreviewConfiguration(server.id, selectedSchema, selectedTableName, selectedUser, true)
    }

    if (server === null || server === undefined) {
        return <Text>Loading...</Text>
    }

    let previewTable = null
    if (previewData !== null && previewData !== undefined) {
        previewTable = <DataTable
            rows={previewData.results.Rows}
            columns={previewData.results.Columns}
            unique_table_id={server.id}
        />
    }

    return <div id="database-preview">
            <div className="filter-options">

                { (schemas && schemas.length > 1) ? <>
                    <Text id={"text-preview"}>Preview schema</Text>
                    <DefaultDropButton
                        name={"Schema"}
                        selected={selectedSchema}
                        options={schemas}
                        label={"schema"}
                        hideLabel
                        errorLoc={[]}
                        onSelect={(newSchema) => {
                            setSelectedSchema(newSchema)
                            setDefaultVal(server.id, "schema", newSchema)
                            updatedTablesList(newSchema)
                        }}
                    />
                    <Text id={"text-preview"}>table</Text>
                </> : <>
                    <Text id={"text-preview"}>Preview table</Text>
                    </>
                }
                <DefaultDropButton
                    name={"Table"}
                    selected={selectedTableName || null}
                    options={tableNames}
                    label={"table"}
                    hideLabel
                    errorLoc={[]}
                    onSelect={(newTableName) => {
                        setSelectedTableName(newTableName)
                        setDefaultVal(server.id, "table", newTableName)
                    }}
                />
                <Text id={"text-as"}>as user</Text>
                <DefaultDropButton
                    name={"User"}
                    selected={selectedUser}
                    options={users.map(user => user.name)}
                    label={"user"}
                    hideLabel
                    errorLoc={[]}
                    onSelect={(newUser) => {
                        setSelectedUser(newUser)
                        setDefaultVal(server.id, "user", newUser)
                    }}
                />
            </div>
            <div className="table-wrapper">
                {loadingPreviewData ? <LoadingSpinner /> : previewTable}
            </div>
    </div>
}



export {Message}
export default DatabasePreview

